<template>
  <div>
    <!-- Entity Type Selector -->
    <div class="bg-white rounded-lg border border-gray-200 mb-4">
      <ComponentHeader title="Select Entity Type" :heading-level="3" />
      <div class="p-4">
      <div class="flex gap-2">
        <select
          v-model="selectedEntityTypeId"
          @change="fetchData"
          class="flex-1 p-2 border border-gray-300 rounded text-sm focus:border-primary-500 focus:ring-1 focus:ring-blue-dark"
        >
          <option disabled value="">Select an entity type</option>
          <option
            v-for="entityType in entityTypes"
            :key="entityType.id"
            :value="entityType.id"
          >
            {{ entityType.type_name }}
          </option>
        </select>
      </div>
      </div>
    </div>

    <div v-if="loading" class="h-96">
      <LoadingIndicator />
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-start gap-3">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="text-red-600 text-xl" />
        <div>
          <h3 class="font-semibold text-red-900">Error</h3>
          <p class="text-sm text-red-700">{{ error }}</p>
        </div>
      </div>
    </div>

    <div v-else-if="entityData.length > 0" class="space-y-4">
      <!-- Contract Filter (shown when attribution data available) -->
      <div v-if="availableContracts && availableContracts.length > 0" class="bg-white rounded-lg border border-gray-200">
        <ComponentHeader title="Filter by Contract" :heading-level="3" />
        <div class="p-4">
        <div class="flex gap-2">
          <select
            v-model="selectedContractId"
            class="flex-1 p-2 border border-gray-300 rounded text-sm focus:border-primary-500 focus:ring-1 focus:ring-blue-dark"
          >
            <option value="">All Contracts</option>
            <option
              v-for="contract in availableContracts"
              :key="contract.contract_id"
              :value="contract.contract_id"
            >
              {{ contract.contract_id }} {{ contract.is_active ? '(Active)' : '(Inactive)' }}
            </option>
          </select>
        </div>
        <p class="text-xs text-gray-500 mt-1">
          {{ selectedContractId ? 'Showing one contract' : `Showing all ${availableContracts.length} contracts` }}
        </p>
        </div>
      </div>

      <!-- InfoTagType Selection Controls -->
      <div class="bg-white rounded-lg border border-gray-200">
        <ComponentHeader title="Select Classification Types" :heading-level="3">
          <FilterTabs
            v-model="sizingMode"
            :options="[
              { value: 'count', label: 'Entity Count' },
              { value: 'attribution', label: 'Attribution' }
            ]"
          />
        </ComponentHeader>
        <div class="p-4">

        <!-- Selected InfoTagTypes -->
        <div class="flex flex-wrap gap-2 mb-3">
          <div
            v-for="(tagTypeId, index) in selectedTagTypeIds"
            :key="tagTypeId"
            class="flex items-center space-x-2 bg-primary-100 text-primary-800 px-3 py-1 rounded-full"
          >
            <span class="text-sm font-medium">{{ getTagTypeName(tagTypeId) }}</span>
            <button
              @click="removeTagType(index)"
              class="text-primary-600 hover:text-primary-800"
            >
              <font-awesome-icon :icon="['fas', 'times']" class="w-3 h-3" />
            </button>
          </div>
        </div>

        <!-- Add InfoTagType Dropdown -->
        <div class="flex gap-2">
          <select
            v-model="availableTagTypeId"
            class="flex-1 p-2 border border-gray-300 rounded text-sm focus:border-primary-500 focus:ring-1 focus:ring-blue-dark"
          >
            <option disabled value="">Select a classification type</option>
            <option
              v-for="tagType in availableTagTypes"
              :key="tagType.tagtype_id"
              :value="tagType.tagtype_id"
              :disabled="isTagTypeSelected(tagType.tagtype_id)"
            >
              {{ tagType.tagtype_name }}
            </option>
          </select>
          <button
            @click="addTagType"
            :disabled="!availableTagTypeId"
            class="btn-primary-sm"
          >
            Add
          </button>
        </div>
        </div>
      </div>

      <!-- Main Visualization Area -->
      <div class="flex gap-4 overflow-hidden" style="height: 500px;">
        <!-- Sunburst Chart (Left 60%) -->
        <div class="bg-white rounded-lg border border-gray-200 p-4 shrink-0" style="width: 60%;">
          <Sunburst
            ref="sunburst"
            :chartData="displayedEntities"
            :leafNameKey="'entity_name'"
            :selectedTagTypeIds="selectedTagTypeIds"
            :sizingMode="sizingMode"
            @segment-clicked="handleSegmentClicked"
          />
        </div>

        <!-- Entity List (Right 40%) -->
        <div class="bg-white rounded-lg border border-gray-200 p-4 flex flex-col shrink-0 overflow-hidden" style="width: 40%;">
          <!-- Search Bar -->
          <div class="mb-4 shrink-0">
            <div class="relative">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search entities..."
                class="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent text-sm"
              >
              <font-awesome-icon
                :icon="['fas', 'magnifying-glass']"
                class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
              />
              <button
                v-if="searchQuery"
                @click="searchQuery = ''"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <font-awesome-icon :icon="['fas', 'times']" class="text-xs" />
              </button>
            </div>
          </div>

          <!-- Entity List (Scrollable) -->
          <div class="flex-1 min-h-0">
            <EntityClassificationList
              :entities="displayedEntities"
              :searchQuery="searchQuery"
              :showAttribution="sizingMode === 'attribution'"
            />
          </div>
        </div>
      </div>

      <!-- Stats Section -->
      <div class="bg-white rounded-lg border border-gray-200">
        <ComponentHeader title="Statistics" :heading-level="3" />
        <div class="p-4">
        <div v-if="sizingMode === 'count'" class="grid grid-cols-3 gap-4">
          <div class="text-center">
            <div class="text-3xl font-bold text-gray-900">{{ displayedEntities.length }}</div>
            <div class="text-xs text-gray-600 mt-1">
              {{ selectedSegment ? 'Filtered Entities' : 'Total Entities' }}
            </div>
          </div>

          <div class="text-center">
            <div class="text-3xl font-bold text-green-600">{{ taggedEntitiesCount }}</div>
            <div class="text-xs text-gray-600 mt-1">Tagged Entities</div>
          </div>

          <div class="text-center">
            <div class="text-3xl font-bold text-orange-600">{{ untaggedEntitiesCount }}</div>
            <div class="text-xs text-gray-600 mt-1">Untagged Entities</div>
          </div>
        </div>

        <div v-else class="grid grid-cols-4 gap-4">
          <div class="text-center">
            <div class="text-3xl font-bold text-primary-900">{{ formatNumber(totalAttribution) }}</div>
            <div class="text-xs text-gray-600 mt-1">Total Attributed Patients</div>
          </div>

          <div class="text-center">
            <div class="text-3xl font-bold text-green-600">{{ entitiesWithAttribution }}</div>
            <div class="text-xs text-gray-600 mt-1">Entities with Attribution</div>
          </div>

          <div class="text-center">
            <div class="text-3xl font-bold text-orange-600">{{ entitiesWithoutAttribution }}</div>
            <div class="text-xs text-gray-600 mt-1">Entities without Attribution</div>
          </div>

          <div class="text-center">
            <div class="text-3xl font-bold text-blue-600">{{ formatNumber(averageAttribution) }}</div>
            <div class="text-xs text-gray-600 mt-1">Avg Attribution per Entity</div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <div v-else-if="selectedEntityTypeId" class="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
      <font-awesome-icon :icon="['fas', 'inbox']" class="text-4xl text-gray-400 mb-3" />
      <h3 class="text-lg font-medium text-gray-900 mb-1">No Data Available</h3>
      <p class="text-gray-600">No entities found for classification</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sunburst from '@/components/widgetcomponents/Viz_Elements/Sunburst.vue';
import EntityClassificationList from './Entity_Classification_List.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'MetricEntityClassification',
  components: {
    Sunburst,
    EntityClassificationList,
    ComponentHeader,
    FilterTabs,
    LoadingIndicator
  },
  data() {
    return {
      loading: false,
      error: null,
      entityData: [],
      entityTypes: [],
      selectedEntityTypeId: '',
      availableTagTypes: [],
      selectedTagTypeIds: [],
      availableTagTypeId: '',
      selectedSegment: null,
      searchQuery: '',
      sizingMode: 'count',
      availableContracts: [],
      selectedContractId: ''
    };
  },
  computed: {
    contractFilteredEntities() {
      let entities = [...this.entityData];

      // Apply contract filter if selected
      if (this.selectedContractId) {
        entities = entities.filter(entity => {
          const contracts = entity.attribution_data?.contracts;
          if (!contracts || !Array.isArray(contracts)) {
            return false;
          }
          return contracts.some(
            contract => contract.contract_id === this.selectedContractId
          );
        });
      }

      return entities;
    },

    displayedEntities() {
      let entities = this.selectedSegment
        ? this.getEntitiesForSegment(this.selectedSegment)
        : this.contractFilteredEntities;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        entities = entities.filter(entity => {
          const name = entity.entity_name || '';
          const id = String(entity.entity_id || '');
          return name.toLowerCase().includes(query) || id.includes(query);
        });
      }

      return entities;
    },

    taggedEntitiesCount() {
      if (this.selectedTagTypeIds.length === 0) return 0;

      return this.displayedEntities.filter(entity => {
        return this.selectedTagTypeIds.every(tagTypeId => {
          const entityTagType = entity.infotagtypes?.find(tt => tt.tagtype_id === tagTypeId);
          return entityTagType && entityTagType.tags && entityTagType.tags.length > 0;
        });
      }).length;
    },

    untaggedEntitiesCount() {
      return this.displayedEntities.length - this.taggedEntitiesCount;
    },

    totalAttribution() {
      return this.displayedEntities.reduce((sum, entity) => {
        return sum + (entity.total_attribution || 0);
      }, 0);
    },

    entitiesWithAttribution() {
      return this.displayedEntities.filter(entity =>
        (entity.total_attribution || 0) > 0
      ).length;
    },

    entitiesWithoutAttribution() {
      return this.displayedEntities.length - this.entitiesWithAttribution;
    },

    averageAttribution() {
      if (this.entitiesWithAttribution === 0) return 0;
      return this.totalAttribution / this.entitiesWithAttribution;
    }
  },
  methods: {
    async fetchEntityTypes() {
      try {
        const response = await axios.get('/api/entity-types/');
        this.entityTypes = response.data;
      } catch (error) {
        console.error('Error fetching entity types:', error);
        this.error = 'Failed to load entity types';
      }
    },

    async fetchData() {
      if (!this.selectedEntityTypeId) return;

      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get('/api/infotagtypes/classification-summary/', {
          params: {
            entity_type_id: this.selectedEntityTypeId,
            include_attribution: true
          }
        });

        if (response.data.success) {
          this.entityData = response.data.data;
          this.detectTagTypes();
          this.detectContracts();
        } else {
          this.error = 'Failed to load classification data';
        }

      } catch (error) {
        console.error('Error fetching classification data:', error);
        this.error = error.response?.data?.error || 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },

    detectTagTypes() {
      const tagTypeMap = new Map();

      this.entityData.forEach(entity => {
        if (entity.infotagtypes && Array.isArray(entity.infotagtypes)) {
          entity.infotagtypes.forEach(tagType => {
            if (!tagTypeMap.has(tagType.tagtype_id)) {
              tagTypeMap.set(tagType.tagtype_id, {
                tagtype_id: tagType.tagtype_id,
                tagtype_name: tagType.tagtype_name,
                tagtype_color: tagType.tagtype_color
              });
            }
          });
        }
      });

      this.availableTagTypes = Array.from(tagTypeMap.values());

      if (this.availableTagTypes.length > 0 && this.selectedTagTypeIds.length === 0) {
        this.selectedTagTypeIds.push(this.availableTagTypes[0].tagtype_id);
      }
    },

    detectContracts() {
      const contractMap = new Map();

      this.entityData.forEach(entity => {
        const contracts = entity.attribution_data?.contracts;
        if (contracts && Array.isArray(contracts)) {
          contracts.forEach(contract => {
            if (contract.contract_id && !contractMap.has(contract.contract_id)) {
              const now = new Date();
              const startDate = contract.attribution_period_start ? new Date(contract.attribution_period_start) : null;
              const endDate = contract.attribution_period_end ? new Date(contract.attribution_period_end) : null;

              const isActive = startDate && endDate && now >= startDate && now <= endDate;

              contractMap.set(contract.contract_id, {
                contract_id: contract.contract_id,
                is_active: isActive,
                entity_count: 1
              });
            } else if (contract.contract_id) {
              const existing = contractMap.get(contract.contract_id);
              if (existing) {
                existing.entity_count += 1;
              }
            }
          });
        }
      });

      this.availableContracts = Array.from(contractMap.values())
        .sort((a, b) => {
          if (a.is_active !== b.is_active) {
            return a.is_active ? -1 : 1;
          }
          return a.contract_id.localeCompare(b.contract_id);
        });

      if (this.selectedContractId && !contractMap.has(this.selectedContractId)) {
        this.selectedContractId = '';
      }
    },

    isTagTypeSelected(tagTypeId) {
      return this.selectedTagTypeIds.includes(tagTypeId);
    },

    addTagType() {
      if (this.availableTagTypeId && !this.isTagTypeSelected(this.availableTagTypeId)) {
        this.selectedTagTypeIds.push(this.availableTagTypeId);
        this.availableTagTypeId = '';
      }
    },

    removeTagType(index) {
      this.selectedTagTypeIds.splice(index, 1);
    },

    getTagTypeName(tagTypeId) {
      const tagType = this.availableTagTypes.find(tt => tt.tagtype_id === tagTypeId);
      return tagType ? tagType.tagtype_name : `TagType ${tagTypeId}`;
    },

    handleSegmentClicked(segment) {
      this.selectedSegment = segment;
    },

    getEntitiesForSegment(segment) {
      if (!segment || !segment.path || segment.path === 'Root' || segment.depth === 0) {
        return this.entityData;
      }

      const pathParts = segment.path.split('/').slice(1);
      const filters = [];

      pathParts.forEach(part => {
        const match = part.match(/^(.+?):\s*(.+)$/);
        if (match) {
          const tagTypeName = match[1].trim();
          const tagValue = match[2].trim();
          const tagTypeId = this.findTagTypeIdByName(tagTypeName);

          if (tagTypeId) {
            filters.push({ tagTypeId, tagValue });
          }
        }
      });

      return this.entityData.filter(entity => {
        return filters.every(filter => {
          const entityTagType = entity.infotagtypes?.find(
            tt => tt.tagtype_id === filter.tagTypeId
          );

          if (filter.tagValue === 'Untagged') {
            return !entityTagType || !entityTagType.tags || entityTagType.tags.length === 0;
          }

          if (!entityTagType || !entityTagType.tags) {
            return false;
          }

          return entityTagType.tags.some(tag => {
            const tagNames = filter.tagValue.split(', ').map(t => t.trim());
            return tagNames.includes(tag.name);
          });
        });
      });
    },

    findTagTypeIdByName(tagTypeName) {
      const tagType = this.availableTagTypes.find(tt => tt.tagtype_name === tagTypeName);
      return tagType ? tagType.tagtype_id : null;
    },

    formatNumber(value) {
      if (value === 0) return '0';
      return Math.round(value).toLocaleString();
    }
  },
  watch: {
    selectedContractId() {
      if (this.sizingMode === 'attribution') {
        this.$forceUpdate();
      }
    }
  },
  mounted() {
    this.fetchEntityTypes();
  }
};
</script>
